import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import {
  EquipmentId,
  type EquipmentMaintenanceLogId,
} from "@brewbase/db/types";
import { trpc } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import { PlusIcon } from "lucide-react";
import { useState, useMemo } from "react";
import { toast } from "sonner";
import { format } from "date-fns";

export const Route = createFileRoute(
  "/inventory/equipment/$equipment_id/maintenance",
)({
  component: MaintenancePage,
  params: z.object({
    equipment_id: z.coerce.number().transform((val) => val as EquipmentId),
  }),
});

function MaintenancePage() {
  const { equipment_id } = Route.useParams();
  const utils = trpc.useUtils();

  const { data: maintenanceLogs } =
    trpc.equipment.maintenanceLog.list.useQuery(equipment_id);

  const deleteMutation = trpc.equipment.maintenanceLog.delete.useMutation({
    onSuccess: () => {
      toast.success("Maintenance log deleted");
      utils.equipment.maintenanceLog.list.invalidate(equipment_id);
    },
    onError: (err) => toast.error(`Failed to delete: ${err.message}`),
  });

  const sortedLogs = useMemo(() => {
    if (!maintenanceLogs) return [];
    return [...maintenanceLogs].sort(
      (a, b) =>
        new Date(b.performed_at).getTime() -
        new Date(a.performed_at).getTime(),
    );
  }, [maintenanceLogs]);

  return (
    <div className="flex-1 p-6 space-y-4 max-w-2xl">
      <h2 className="text-sm font-medium">Maintenance Log</h2>

      <AddMaintenanceButton equipmentId={equipment_id} />

      {sortedLogs.length > 0 ? (
        <div className="space-y-2">
          {sortedLogs.map((log) => (
            <div
              key={log.id}
              className="border border-olive-6 p-3 text-sm space-y-1"
            >
              <div className="flex justify-between items-start">
                <span className="font-medium">{log.description}</span>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-xs">
                    {format(new Date(log.performed_at), "MMM d, yyyy")}
                  </span>
                  <DeleteConfirmDialog
                    description={`Delete maintenance log "${log.description}"? This cannot be undone.`}
                    onConfirm={() =>
                      deleteMutation.mutate(
                        log.id as EquipmentMaintenanceLogId,
                      )
                    }
                    isDeleting={deleteMutation.isPending}
                  />
                </div>
              </div>
              {log.notes && (
                <p className="text-muted-foreground">{log.notes}</p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          No maintenance logs recorded.
        </p>
      )}
    </div>
  );
}

function AddMaintenanceButton({ equipmentId }: { equipmentId: EquipmentId }) {
  const utils = trpc.useUtils();
  const [description, setDescription] = useState("");

  const createMutation = trpc.equipment.maintenanceLog.create.useMutation({
    onSuccess: () => {
      toast.success("Log added");
      utils.equipment.maintenanceLog.list.invalidate(equipmentId);
      setDescription("");
    },
    onError: (err) => {
      toast.error(`Failed to add: ${err.message}`);
    },
  });

  const handleAdd = () => {
    if (!description.trim()) return;
    createMutation.mutate({
      equipmentId,
      performedAt: new Date(),
      description,
    });
  };

  return (
    <div className="flex gap-2">
      <Input
        placeholder="Add maintenance note..."
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        className="h-8"
      />
      <Button
        variant="outline"
        size="sm"
        onClick={handleAdd}
        disabled={createMutation.isPending || !description.trim()}
      >
        <PlusIcon className="h-4 w-4" />
      </Button>
    </div>
  );
}
